import { Capacitor } from '@capacitor/core'
import { useSupabase } from '@packages/supabase'
import type { AuthError } from '@supabase/supabase-js'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { sendSupabasePasswordReset } from '../passwordReset'
import { getAuthRedirectUrl } from '../redirect'
import type {
  AuthSignInContext,
  AuthSignInFormActions,
  AuthSignInFormState,
  AuthSignInView,
  UseAuthSignInOptions
} from '../types'

export function useAuthSignIn(
  options: UseAuthSignInOptions = {}
): AuthSignInContext {
  const { supabase } = useSupabase()
  const { t } = useTranslation()

  const redirectTo =
    options.redirectTo ?? getAuthRedirectUrl()
  const features = {
    google: options.features?.google ?? true,
    passwordGate: options.features?.passwordGate ?? false,
    forgotPassword: options.features?.forgotPassword ?? true
  }

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<AuthError | string | null>(null)
  const [resultMessage, setResultMessage] = useState<string | null>(null)
  const [view, setView] = useState<AuthSignInView>('sign_in')
  const [showPasswordLogin, setShowPasswordLogin] = useState(
    !features.passwordGate
  )

  const translateError = useCallback(
    (err: AuthError | string | null): string | null => {
      if (!err) return null
      if (typeof err === 'string') return err
      return t(`auth.errors.${err.code}`, {
        defaultValue: err.message
      })
    },
    [t]
  )

  const signInWithPassword = useCallback(async () => {
    setLoading(true)
    setError(null)

    const { data, error: signInError } = await supabase.auth.signInWithPassword(
      {
        email,
        password
      }
    )

    setLoading(false)
    if (signInError) {
      setError(signInError)
      return
    }

    options.onEmailSignInSuccess?.({
      user: data.user,
      session: data.session
    })
  }, [supabase, email, password, options])

  const signInWithGoogle = useCallback(async () => {
    setError(null)
    const isNative = Capacitor.isNativePlatform()

    if (options.oauthFlow) {
      sessionStorage.setItem('oauth_flow', options.oauthFlow)
    }

    const { data, error: oauthError } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        scopes: 'email, profile',
        queryParams: {
          prompt: 'select_account',
          ...options.googleQueryParams
        },
        redirectTo,
        ...(isNative ? { skipBrowserRedirect: true } : {})
      }
    })

    if (isNative && data?.url && options.openOAuthBrowser) {
      await options.openOAuthBrowser(data.url)
    }

    if (!oauthError) {
      options.onGoogleSignInStart?.()
    } else {
      setError(oauthError)
    }
  }, [supabase, redirectTo, options])

  const sendPasswordResetEmail = useCallback(async () => {
    setLoading(true)
    setError(null)

    const result = await sendSupabasePasswordReset(supabase, email, redirectTo)

    setLoading(false)

    if (!result.success) {
      setError(result.error ?? 'Failed to send reset email')
      return
    }

    setResultMessage(
      t('auth.reset_password_email_sent.label', {
        defaultValue:
          'Password reset request sent. Please check your email.'
      })
    )
  }, [supabase, email, redirectTo, t])

  const state = useMemo<AuthSignInFormState>(
    () => ({
      email,
      password,
      loading,
      error,
      resultMessage,
      view,
      showPasswordLogin,
      redirectTo
    }),
    [
      email,
      password,
      loading,
      error,
      resultMessage,
      view,
      showPasswordLogin,
      redirectTo
    ]
  )

  const actions = useMemo<AuthSignInFormActions>(
    () => ({
      setEmail,
      setPassword,
      setView,
      setShowPasswordLogin,
      setError,
      clearError: () => setError(null),
      signInWithPassword,
      signInWithGoogle,
      sendPasswordResetEmail,
      translateError
    }),
    [
      signInWithPassword,
      signInWithGoogle,
      sendPasswordResetEmail,
      translateError
    ]
  )

  return { state, actions }
}
